<!--
 * @Description: app 布局入口
-->
<template>
  <div
    :class="classObj"
    class="app-wrapper"
  >
    <div
      v-if="classObj.mobile && sidebar.opened"
      class="drawer-bg"
      @click="handleClickOutside"
    />
    <Sidebar class="sidebar-container" />
    <div
      :class="{hasTagsView: showTagsView}"
      class="main-container"
    >
      <div :class="{'fixed-header': fixedHeader}">
        <Navbar />
        <TagsView v-if="showTagsView" />
      </div>
      <AppMain />
      <div :class="fixedHeader ? 'footer-fixed' : 'footer'">
        <div class="footer-bottom">
          <div class="footer-links">
            <div class="copyright">
              {{ copyright }}
            </div>
            <div class="version">
              {{ version }}
            </div>
          </div>
        </div>
        <div class="footer-bottom">
          <div class="footer-links">
            <el-link
              href="https://beian.miit.gov.cn/"
              target="_blank"
            >
              苏ICP备2026026075号
            </el-link>
            <el-link
              href="https://beian.mps.gov.cn/#/query/webSearch"
              target="_blank"
            >
              苏公网安备32041102001822号
            </el-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import config from '@/../package.json'
import { DeviceType } from '@/store/modules/app/state'
import { computed, defineComponent, onBeforeMount, onBeforeUnmount, onMounted, reactive, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from '@/store'
import { AppActionTypes } from '@/store/modules/app/action-types'
import { AppMain, Navbar, TagsView, Sidebar } from './components'
import resize from './resize'
export default defineComponent({
  name: 'Layout',
  components: {
    AppMain,
    Navbar,
    Sidebar,
    TagsView
  },
  setup() {
    const { t } = useI18n()
    const store = useStore()
    const copyright = config.copyright
    const version = config.version
    const { sidebar, device, addEventListenerOnResize, resizeMounted, removeEventListenerResize, watchRouter, isMobile } = resize()
    const state = reactive({
      handleClickOutside: () => {
        store.dispatch(AppActionTypes.ACTION_CLOSE_SIDEBAR, false)
      }
    })

    const classObj = computed(() => {
      return {
        hideSidebar: !sidebar.value.opened,
        openSidebar: sidebar.value.opened,
        withoutAnimation: sidebar.value.withoutAnimation,
        mobile: device.value === DeviceType.Mobile
      }
    })

    const showTagsView = computed(() => {
      return store.state.settings.showTagsView && !isMobile()
    })
    const isMobileDevices = computed(() => {
      return isMobile()
    })
    const fixedHeader = computed(() => {
      return store.state.settings.fixedHeader
    })

    watchRouter()
    onBeforeMount(() => {
      addEventListenerOnResize()
    })

    onMounted(() => {
      resizeMounted()
    })

    onBeforeUnmount(() => {
      removeEventListenerResize()
    })
    return {
      t,
      classObj,
      sidebar,
      showTagsView,
      fixedHeader,
      isMobileDevices,
      copyright,
      version,
      ...toRefs(state)
    }
  }
})
</script>

<style lang="scss" scoped>
@import '../styles/mixins.scss';
@import '../styles/_variables.scss';

.app-wrapper {
  @include clearfix;
  position: relative;
  height: 100%;
  width: 100%;
}

.drawer-bg {
  background: #000;
  opacity: 0.3;
  width: 100%;
  top: 0;
  height: 100%;
  position: absolute;
  z-index: 999;
}

.main-container {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  transition: margin-left .28s;
  margin-left: $sideBarWidth;
  position: relative;
}

.sidebar-container {
  transition: width 0.28s;
  width: $sideBarWidth !important;
  height: 100%;
  position: fixed;
  font-size: 0;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 1001;
  overflow: hidden;
  background-color: #ffffff !important;
}

.footer {
  min-height: 30px;
  bottom: 0;
  width: 100%;
  overflow: hidden;
  margin-top: auto;
  position: sticky;
  background: #fff;
  color: #909399;
  z-index: 1002;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  padding: 5px 30px;
}

.footer-fixed {
  min-height: 30px;
  bottom: 0;
  transition: width 0.28s;
  width: 100%;
  overflow: hidden;
  position: sticky;
  background: #fff;
  color: #909399;
  z-index: 1002;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  padding: 5px 30px;
  box-sizing: border-box;
  flex-shrink: 0;
  margin-top: auto;
}
.footer-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.footer-bottom {
  font-size: 13px;
  color: #999;
  text-align: center;
}
.copyright {
  line-height:22px;
  font-size: 13px;
}
.version {
  line-height:22px;
  font-size: 13px;
}
.footer-links {
  text-align: center;
  display: flex;
  justify-content: center;
  gap: 20px;
  .el-link {
    color: #999;

    &:hover {
      color: #0066CC;
    }
  }
}
.fixed-header {
  position: sticky;
  top: 0;
  right: 0;
  z-index: 9;
  width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;
  transition: width 0.28s;
}

.layout-main{
  flex: 1; /* 关键：占据剩余空间 */
  overflow: auto; /* 滚动在此生效 */
  min-height: 0;
}

.hideSidebar {
  .main-container {
    margin-left: 54px;
  }

  .sidebar-container {
    width: 54px !important;
  }
}

/* for mobile response 适配移动端 */
.mobile {
  .main-container {
    margin-left: 0;
  }

  .sidebar-container {
    transition: transform .28s;
    width: $sideBarWidth !important;
  }

  &.openSidebar {
    position: fixed;
    top: 0;
  }

  &.hideSidebar {
    .sidebar-container {
      pointer-events: none;
      transition-duration: 0.3s;
      transform: translate3d(-$sideBarWidth, 0, 0);
    }
  }

  .footer {
    width: 100%;
    margin-top: auto;
    position: sticky;
  }
  .footer-fixed {
    width: 100%;
    margin-top: auto;
    position: sticky;
  }
}

.withoutAnimation {
  .main-container,
  .sidebar-container {
    transition: none;
  }
}

</style>
